import { useState, useCallback, useMemo, useRef } from 'react';

const TOAST_SEVERITIES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

const INITIAL_TOAST_STATE = {
  open: false,
  message: '',
  severity: TOAST_SEVERITIES.INFO,
};

export const useToast = () => {
  const [toastState, setToastState] = useState(INITIAL_TOAST_STATE);
  const timeoutRef = useRef(null);

  const showToast = useCallback((message, severity = TOAST_SEVERITIES.INFO, duration = 6000) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setToastState({
      open: true,
      message,
      severity,
    });

    if (duration > 0) {
      timeoutRef.current = setTimeout(() => {
        setToastState((prev) => ({
          ...prev,
          open: false,
        }));
      }, duration);
    }
  }, []);

  const hideToast = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setToastState((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const showSuccess = useCallback(
    (message, duration) => {
      showToast(message, TOAST_SEVERITIES.SUCCESS, duration);
    },
    [showToast]
  );

  const showError = useCallback(
    (message, duration) => {
      showToast(message, TOAST_SEVERITIES.ERROR, duration);
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message, duration) => {
      showToast(message, TOAST_SEVERITIES.WARNING, duration);
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message, duration) => {
      showToast(message, TOAST_SEVERITIES.INFO, duration);
    },
    [showToast]
  );

  const showCustomToast = useCallback(
    (config) => {
      const { message, severity = TOAST_SEVERITIES.INFO, duration = 6000 } = config;
      showToast(message, severity, duration);
    },
    [showToast]
  );

  const toastAPI = useMemo(
    () => ({
      toastState,
      showToast,
      hideToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showCustomToast,
      isOpen: toastState.open,
      severity: toastState.severity,
      message: toastState.message,
    }),
    [
      toastState,
      showToast,
      hideToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showCustomToast,
    ]
  );

  return toastAPI;
};

export const useFormToast = () => {
  const toast = useToast();

  const formToastAPI = useMemo(
    () => ({
      ...toast,
      showValidationError: (message = 'Por favor, completa todos los campos obligatorios.') => {
        toast.showError(message, 5000);
      },
      showSubmissionSuccess: (message = '¡Formulario enviado exitosamente!') => {
        toast.showSuccess(message, 4000);
      },
      showSubmissionError: (
        message = 'Error al enviar el formulario. Por favor, inténtalo de nuevo.'
      ) => {
        toast.showError(message, 6000);
      },
      showNetworkError: (message = 'Error de conexión. Verifica tu conexión a internet.') => {
        toast.showError(message, 8000);
      },
    }),
    [toast]
  );

  return formToastAPI;
};
